import type {
  ProductImageArea,
  ProductImageEntry,
  ProductImageScenario,
  ProductImageText,
} from "./types";

/**
 * Import, onboarding and operations surfaces from the 2026-10 surface
 * campaign. Onboarding pages run in S5, import states in S4 against the
 * accepted synthetic import receipt (uploads stop at the preview, nothing is
 * applied), and operations in S3 on the published week with its sick day.
 */
type Draft = {
  id: string;
  module: string;
  opens: string;
  setup: readonly string[];
  scenario: ProductImageScenario;
  area: ProductImageArea;
  sv: ProductImageText;
  en: ProductImageText;
};

function entry(draft: Draft): ProductImageEntry {
  return {
    id: draft.id,
    module: draft.module,
    area: draft.area,
    opens: draft.opens,
    setup: draft.setup,
    scenario: draft.scenario,
    visual: { variant: 0 },
    text: { sv: draft.sv, en: draft.en },
    use: ["help"],
  };
}

const PREVIEW_SV = "Inget sparas förrän planeraren godkänner importen.";
const PREVIEW_EN = "Nothing is saved until the planner accepts the import.";
const IMPORT = "Importera → Ladda upp besökshistorik";
const OPERATIONS = "Drift → publicerad arbetsdag";
const STAFFING = "Drift → publicerad arbetsdag → Bemanning";
const INBOXES = "Schema → Att hantera";

export const CAMPAIGN_IMPORT_OPS_CATALOGUE: readonly ProductImageEntry[] = [
  // ── Kom igång ─────────────────────────────────────────────────────────
  entry({
    id: "onboarding.welcome",
    module: "Välkommen",
    opens: "/welcome",
    setup: ["Öppna välkomstsidan för den inloggade organisationen"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Välkomstsidan med vägarna in i Caire",
      shows:
        "Välkomstsidan där en ny organisation väljer om den vill börja planera eller ladda upp besökshistorik.",
      proves:
        "Första inloggningen leder direkt till ett val av nästa steg, utan väntan.",
    },
    en: {
      alt: "The welcome page with the ways into Caire",
      shows:
        "The welcome page where a new organisation chooses to start planning or upload visit history.",
      proves:
        "The first sign-in leads straight to a choice of next step, without waiting.",
    },
  }),
  entry({
    id: "onboarding.create-organization",
    module: "Lägg till organisation",
    opens: "/create-organization?convert=1",
    setup: ["Öppna formuläret för att lägga till en organisation"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Formuläret för organisationens namn, organisationsnummer och adress",
      shows:
        "Formuläret där organisationen fyller i namn, organisationsnummer, kontaktuppgifter och kontorsadress.",
      proves:
        "Kontorsadressen blir utgångspunkt för rutterna redan när organisationen läggs upp. Inget sparas förrän formuläret skickas.",
    },
    en: {
      alt: "The form for the organisation's name, registration number and address",
      shows:
        "The form where the organisation enters its name, registration number, contact details and office address.",
      proves:
        "The office address becomes the starting point for routes as soon as the organisation is set up. Nothing is saved until the form is sent.",
    },
  }),
  entry({
    id: "onboarding.invite-colleagues",
    module: "Bjud in kollegor",
    opens: "/start/invite",
    setup: ["Öppna steget Bjud in kollegor i Kom igång"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Steget Bjud in kollegor med e-post och behörighet",
      shows:
        "Steget där planeraren lägger till kollegors e-post och väljer vilken behörighet var och en får.",
      proves:
        "Behörigheten avgör vad kollegan kan göra: samordnare planerar och publicerar, administratörer hanterar även konto och medlemmar. Inga inbjudningar skickas i bilden.",
    },
    en: {
      alt: "The Invite colleagues step with email and permission",
      shows:
        "The step where the planner adds colleagues' email addresses and chooses each person's permission.",
      proves:
        "The permission decides what a colleague can do: coordinators plan and publish, administrators also manage the account and members. No invitations are sent in the image.",
    },
  }),
  entry({
    id: "onboarding.demo-tour",
    module: "Demorundtur",
    opens: "/start → Utforska demon",
    setup: ["Starta demorundturen från Kom igång"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Demorundturens första steg över planeringen",
      shows:
        "Rundturen som leder genom demoenheten steg för steg: baslinjen, analysen, Caires resultat och jämförelsen.",
      proves:
        "Demon visar samma besök före och efter optimering i den riktiga produkten, utan att något ändras.",
    },
    en: {
      alt: "The first step of the demo tour over the plan",
      shows:
        "The tour that walks through the demo unit step by step: the baseline, the analysis, Caire's result and the comparison.",
      proves:
        "The demo shows the same visits before and after optimisation in the real product, without changing anything.",
    },
  }),

  // ── Import av besökshistorik ──────────────────────────────────────────
  entry({
    id: "import.data-handling",
    module: "Trygg hantering av rapporterna",
    opens: IMPORT,
    setup: [
      "Välj Alfa eCare Welfare",
      "Öppna Detaljer om skydd och leverantörer",
    ],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Hur rapporterna skyddas, uppdelat på vad som sparas och vem som ser det",
      shows:
        "Rutan som beskriver vad importen sparar, vad som tas bort, vem som får se uppgifterna och vilka leverantörer som används.",
      proves:
        "Hanteringen av personuppgifter står i klartext innan någon fil laddas upp.",
    },
    en: {
      alt: "How the reports are protected, split by what is kept and who sees it",
      shows:
        "The panel that describes what the import keeps, what is dropped, who may see the data and which providers are used.",
      proves:
        "How personal data is handled is stated plainly before any file is uploaded.",
    },
  }),
  entry({
    id: "import.source-help",
    module: "Ta ut rapporter",
    opens: IMPORT,
    setup: ["Välj Alfa eCare Welfare", "Öppna exportguiden för en rapport"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Exportguiden som visar var rapporten finns i verksamhetssystemet",
      shows:
        "Guiden som visar steg för steg var rapporten tas ut i verksamhetssystemet, med sökväg och bild.",
      proves: "Planeraren kan hämta rätt rapport själv, utan support.",
    },
    en: {
      alt: "The export guide showing where the report is found in the care system",
      shows:
        "The guide that shows step by step where the report is exported in the care system, with path and picture.",
      proves: "The planner can fetch the right report without support.",
    },
  }),
  entry({
    id: "import.analysing",
    module: "Caire analyserar rapporterna",
    opens: IMPORT,
    setup: [
      "Välj Alfa eCare Welfare",
      "Lägg till syntetiska rapportfiler",
      "Välj Analysera rapporterna",
    ],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Väntläget medan Caire läser de uppladdade rapporterna",
      shows:
        "Läget medan Caire läser besöken, kopplar kundregistret och rensar adresser, med förfluten tid.",
      proves: `Planeraren ser att analysen pågår och hur länge den har hållit på. ${PREVIEW_SV}`,
    },
    en: {
      alt: "The waiting state while Caire reads the uploaded reports",
      shows:
        "The state while Caire reads the visits, joins the client register and cleans addresses, with elapsed time.",
      proves: `The planner sees that the analysis is running and for how long. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.existing-records",
    module: "Befintliga poster",
    opens: `${IMPORT} → granska`,
    setup: [
      "Ladda upp en ny period till en enhet som redan har importerade poster",
      "Analysera rapporterna",
    ],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Befintliga poster i enheten bredvid de nya från rapporterna",
      shows:
        "Panelen som visar vilka medarbetare och kunder som redan finns i enheten och hur de möter de nya raderna.",
      proves: `Importen skapar inga dubbletter i det tysta; planeraren ser krocken först. ${PREVIEW_SV}`,
    },
    en: {
      alt: "Existing records in the unit beside the new ones from the reports",
      shows:
        "The panel that shows which employees and clients already exist in the unit and how they meet the new rows.",
      proves: `The import never creates duplicates silently; the planner sees the conflict first. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.version-review",
    module: "Korrigering av importerad period",
    opens: `${IMPORT} → granska`,
    setup: [
      "Ladda upp rapporter för en period som enheten redan har",
      "Analysera rapporterna",
    ],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Granskning av en korrigerad period innan den ersätter den tidigare",
      shows:
        "Rutan som visar att rapporterna gäller en period som redan finns och vad en korrigering ändrar.",
      proves: `En sparad period ersätts bara när planeraren uttryckligen bekräftar korrigeringen. ${PREVIEW_SV}`,
    },
    en: {
      alt: "Review of a corrected period before it replaces the earlier one",
      shows:
        "The panel that shows the reports cover a period that already exists and what a correction changes.",
      proves: `A saved period is replaced only when the planner explicitly confirms the correction. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.skipped-rows",
    module: "Överhoppade rader",
    opens: `${IMPORT} → granska → detaljer`,
    setup: ["Analysera rapporterna", "Öppna detaljerna i granskningen"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Rader i rapporterna som importen hoppade över, med skäl",
      shows:
        "Listan över rader som inte kunde användas och skälet för varje rad.",
      proves: `Inget försvinner ur underlaget utan att planeraren kan se varför. ${PREVIEW_SV}`,
    },
    en: {
      alt: "Rows in the reports the import skipped, with reasons",
      shows: "The list of rows that could not be used and the reason for each.",
      proves: `Nothing leaves the source data without the planner seeing why. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.carefox-review",
    module: "Granska Carefox-rapporten",
    opens: `${IMPORT} → Carefox → granska`,
    setup: [
      "Välj Carefox",
      "Lägg till de syntetiska Carefox-rapporterna",
      "Analysera rapporterna",
    ],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Granskningen av Carefox-rapporterna med det som behöver beslut",
      shows:
        "Granskningen av Carefox-underlaget: vad som lästes in och vilka rader som behöver rättas eller beslutas.",
      proves: `Planeraren rättar underlaget i Caire innan något sparas. ${PREVIEW_SV}`,
    },
    en: {
      alt: "The Carefox report review with what needs a decision",
      shows:
        "The review of the Carefox data: what was read and which rows need a correction or a decision.",
      proves: `The planner corrects the data in Caire before anything is saved. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.carefox-quality",
    module: "Importkvalitet",
    opens: `${IMPORT} → Carefox → granska`,
    setup: ["Analysera de syntetiska Carefox-rapporterna"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Importkvaliteten för Carefox-underlaget",
      shows:
        "Panelen som sammanfattar hur komplett Carefox-underlaget är: besök, tider, adresser och personal.",
      proves: `Planeraren ser underlagets kvalitet innan perioden används i analysen. ${PREVIEW_SV}`,
    },
    en: {
      alt: "Import quality for the Carefox data",
      shows:
        "The panel summarising how complete the Carefox data is: visits, times, addresses and staff.",
      proves: `The planner sees the data quality before the period is used in the analysis. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.carefox-confirmation",
    module: "Bekräfta Carefox-import",
    opens: `${IMPORT} → Carefox → bekräfta`,
    setup: ["Analysera de syntetiska Carefox-rapporterna"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Bekräftelsesteget för en Carefox-import",
      shows:
        "Steget där planeraren går igenom perioden, enheten och resurserna innan Carefox-importen sparas.",
      proves: `Perioden sparas först när planeraren har granskat och godkänt den. ${PREVIEW_SV}`,
    },
    en: {
      alt: "The confirmation step of a Carefox import",
      shows:
        "The step where the planner goes through the period, the unit and the resources before the Carefox import is saved.",
      proves: `The period is saved only once the planner has reviewed and accepted it. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.shift-template-review",
    module: "Arbetstid och raster",
    opens: `${IMPORT} → granska → arbetspassmallar`,
    setup: ["Analysera de syntetiska Carefox-rapporterna"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Granskning av arbetstid och raster från de importerade passen",
      shows:
        "Den valfria granskningen av passens arbetstid och raster som Caire läste ut ur rapporterna.",
      proves: `Planeraren kan justera arbetstid och raster innan de blir arbetspassmallar. ${PREVIEW_SV}`,
    },
    en: {
      alt: "Review of working time and breaks from the imported shifts",
      shows:
        "The optional review of the shifts' working time and breaks that Caire read from the reports.",
      proves: `The planner can adjust working time and breaks before they become shift templates. ${PREVIEW_EN}`,
    },
  }),
  entry({
    id: "import.analysis",
    module: "Analys av importerat underlag",
    opens: "Importera → Så här ser perioden ut",
    setup: ["Öppna analysen för den godkända importerade perioden"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Analysen av den importerade perioden",
      shows:
        "Sidan som visar hur den importerade perioden ser ut: besök, personal, tider och vad som behöver åtgärdas.",
      proves:
        "Den egna perioden analyseras i Caire utan att något sparas i de ordinarie enheterna.",
    },
    en: {
      alt: "The analysis of the imported period",
      shows:
        "The page that shows what the imported period looks like: visits, staff, times and what needs attention.",
      proves:
        "Your own period is analysed in Caire without saving anything in the regular units.",
    },
  }),
  entry({
    id: "import.snapshot-pricing",
    module: "Pris för analysen",
    opens: "Importera → Så här ser perioden ut → pris",
    setup: ["Öppna analysen för den godkända importerade perioden"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Prisberäkningen för den importerade periodens plan och utfall",
      shows:
        "Panelen som räknar fram vad planen och det utförda kostade, sida vid sida.",
      proves:
        "Kostnaden för den egna perioden blir jämförelsepunkt för Caires förslag.",
    },
    en: {
      alt: "The pricing of the imported period's plan and actuals",
      shows:
        "The panel that works out what the plan and the performed work cost, side by side.",
      proves:
        "The cost of your own period becomes the reference point for Caire's proposals.",
    },
  }),
  entry({
    id: "import.address-remediation",
    module: "Rätta kundadresser",
    opens: "Importera → Så här ser perioden ut → Adresser",
    setup: ["Öppna analysen för den godkända importerade perioden"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Panelen för att rätta kundadresser i den importerade perioden",
      shows:
        "Panelen där kundadresser som inte kunde placeras på kartan rättas.",
      proves:
        "Besök utan säker adress räknas inte in förrän adressen är rättad.",
    },
    en: {
      alt: "The panel for fixing client addresses in the imported period",
      shows:
        "The panel where client addresses that could not be placed on the map are fixed.",
      proves:
        "Visits without a reliable address are not counted until the address is fixed.",
    },
  }),
  entry({
    id: "import.background-run",
    module: "Analysen fortsätter i bakgrunden",
    opens: "Importera → Så här ser perioden ut → status",
    setup: ["Öppna analysen för den godkända importerade perioden"],
    scenario: "S4",
    area: "operations",
    sv: {
      alt: "Status för analysen som fortsätter i bakgrunden",
      shows:
        "Statusen för optimeringen av den importerade perioden medan Caire arbetar vidare i bakgrunden.",
      proves:
        "Planeraren kan lämna sidan; resultatet finns kvar när optimeringen är klar.",
    },
    en: {
      alt: "Status of the analysis continuing in the background",
      shows:
        "The status of the imported period's optimisation while Caire keeps working in the background.",
      proves:
        "The planner can leave the page; the result is there when the optimisation is done.",
    },
  }),

  // ── Drift och bemanning ───────────────────────────────────────────────
  entry({
    id: "operations.published-overview",
    module: "Översikt",
    opens: `${OPERATIONS} → Översikt`,
    setup: ["Öppna den publicerade arbetsdagen", "Välj Översikt"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Översikten för den publicerade planen",
      shows:
        "Översikten för den publicerade dagen: vilken plan som gäller och när den publicerades.",
      proves: "Det är tydligt vilken plan verksamheten arbetar efter.",
    },
    en: {
      alt: "The overview of the published plan",
      shows:
        "The overview of the published day: which plan applies and when it was published.",
      proves: "It is clear which plan the organisation is working to.",
    },
  }),
  entry({
    id: "operations.declare-gap-form",
    module: "Deklarera lucka",
    opens: `${STAFFING} → Deklarera lucka`,
    setup: ["Öppna Bemanning", "Välj Deklarera lucka"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Formuläret för att deklarera en bemanningslucka",
      shows:
        "Formuläret där planeraren anger vem som är borta och vilken tid, så att berörda besök blir en lucka.",
      proves:
        "Frånvaro som kommer in på annat sätt än via appen hanteras i samma flöde. Inget sparas förrän formuläret skickas.",
    },
    en: {
      alt: "The form for declaring a staffing gap",
      shows:
        "The form where the planner states who is away and when, so the affected visits become a gap.",
      proves:
        "Absence reported outside the app is handled in the same flow. Nothing is saved until the form is sent.",
    },
  }),
  entry({
    id: "operations.staffing-call-list",
    module: "Ringlista och inbjudan",
    opens: `${STAFFING} → Bjud in`,
    setup: ["Öppna Bemanning", "Välj Bjud in på en lucka"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Inbjudan till ett öppet pass och ringlistan för luckan",
      shows:
        "Inbjudan till ett öppet pass med föreslagna medarbetare, och ringlistan med de som behöver tillfrågas personligen.",
      proves:
        "Planeraren ser vem som passar innan någon kontaktas. Ingen inbjudan skickas i bilden.",
    },
    en: {
      alt: "The open-shift invitation and the call list for the gap",
      shows:
        "The open-shift invitation with suggested employees, and the call list of people who need to be asked in person.",
      proves:
        "The planner sees who fits before anyone is contacted. No invitation is sent in the image.",
    },
  }),
  entry({
    id: "operations.revision-sheet",
    module: "Förbered justering",
    opens: `${OPERATIONS} → Hantera avvikelse`,
    setup: ["Öppna den publicerade arbetsdagen", "Välj Hantera avvikelse"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Panelen för att förbereda en justering av den publicerade planen",
      shows:
        "Panelen där planeraren väljer vilken ändring som ska göras i dagens plan, till exempel en försening.",
      proves:
        "Ändringar i den publicerade planen förbereds först och tillämpas aldrig direkt.",
    },
    en: {
      alt: "The panel for preparing an adjustment to the published plan",
      shows:
        "The panel where the planner chooses which change to make to today's plan, such as a delay.",
      proves:
        "Changes to the published plan are prepared first and never applied directly.",
    },
  }),
  entry({
    id: "operations.revision-review",
    module: "Granska justering",
    opens: `${OPERATIONS} → Hantera avvikelse → Förbered justering`,
    setup: [
      "Öppna Hantera avvikelse",
      "Välj en ändring och ett besök",
      "Välj Förbered justering",
    ],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Granskningen av en förberedd justering med vad som ändras",
      shows:
        "Granskningen som visar exakt vilka besök och pass justeringen ändrar innan den tillämpas.",
      proves:
        "Planeraren godkänner varje ändring i den publicerade planen med effekten framför sig.",
    },
    en: {
      alt: "The review of a prepared adjustment with what changes",
      shows:
        "The review that shows exactly which visits and shifts the adjustment changes before it is applied.",
      proves:
        "The planner approves every change to the published plan with its effect in front of them.",
    },
  }),
  entry({
    id: "operations.inboxes",
    module: "Att hantera",
    opens: INBOXES,
    setup: ["Öppna schemat med den publicerade planen"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Schemats inkorgar med det som väntar på beslut",
      shows:
        "Inkorgarna på schemat där sjukanmälningar och andra förfrågningar från appen väntar på planerarens beslut.",
      proves: "Allt som påverkar planen samlas på ett ställe.",
    },
    en: {
      alt: "The schedule's inboxes with what is waiting for a decision",
      shows:
        "The schedule's inboxes where sick reports and other requests from the app wait for the planner's decision.",
      proves: "Everything that affects the plan is gathered in one place.",
    },
  }),
  entry({
    id: "operations.inbox-sick-leave",
    module: "Sjukanmälningar att hantera",
    opens: `${INBOXES} → Sjukanmälningar`,
    setup: ["Rapportera en syntetisk sjukdag", "Öppna schemat"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Sjukanmälningar som väntar på beslut",
      shows:
        "Listan över sjukanmälningar med medarbetare, dagar och om anmälan redan hanteras under Bemanning.",
      proves:
        "En sjukanmälan ändrar ingenting i planen förrän planeraren har beslutat.",
    },
    en: {
      alt: "Sick reports waiting for a decision",
      shows:
        "The list of sick reports with employee, days and whether the report is already handled under Staffing.",
      proves:
        "A sick report changes nothing in the plan until the planner has decided.",
    },
  }),
  entry({
    id: "operations.inbox-shift-swap",
    module: "Skiftbytesförfrågningar att hantera",
    opens: `${INBOXES} → Skiftbyten`,
    setup: ["En medarbetare ber om skiftbyte i appen", "Öppna schemat"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Skiftbytesförfrågningar från medarbetare",
      shows:
        "Förfrågningar om skiftbyte från appen med pass, medarbetare och skäl.",
      proves:
        "Ett skiftbyte blir giltigt först när planeraren har godkänt det.",
    },
    en: {
      alt: "Shift swap requests from employees",
      shows:
        "Shift swap requests from the app with shift, employee and reason.",
      proves:
        "A shift swap takes effect only once the planner has approved it.",
    },
  }),
  entry({
    id: "operations.inbox-availability-offers",
    module: "Erbjudna extrapass",
    opens: `${INBOXES} → Erbjudna extrapass`,
    setup: ["En medarbetare erbjuder ett extrapass i appen", "Öppna schemat"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Extrapass som medarbetare har erbjudit",
      shows:
        "Extrapass som medarbetare har erbjudit i appen, med datum och arbetspassmall.",
      proves:
        "Ett godkänt extrapass blir ny tillgänglig tid som planen kan använda.",
    },
    en: {
      alt: "Extra shifts offered by employees",
      shows:
        "Extra shifts employees have offered in the app, with date and shift template.",
      proves: "An accepted extra shift becomes new capacity the plan can use.",
    },
  }),
  entry({
    id: "operations.inbox-visit-cancellations",
    module: "Avbokningsförfrågningar",
    opens: `${INBOXES} → Avbokningar`,
    setup: ["En kund begär avbokning av ett besök i appen", "Öppna schemat"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Avbokningsförfrågningar från kunder",
      shows: "Kunders förfrågningar om att avboka besök, med besök och skäl.",
      proves:
        "Ett besök försvinner inte ur planen förrän planeraren har godkänt avbokningen.",
    },
    en: {
      alt: "Cancellation requests from clients",
      shows: "Clients' requests to cancel visits, with visit and reason.",
      proves:
        "A visit stays in the plan until the planner has approved the cancellation.",
    },
  }),
  entry({
    id: "operations.inbox-visit-reschedule",
    module: "Önskemål om besökstid",
    opens: `${INBOXES} → Önskemål om besökstid`,
    setup: ["En kund önskar en annan besökstid i appen", "Öppna schemat"],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Önskemål om ny besökstid från kunder",
      shows: "Kunders önskemål om en annan tid för ett besök.",
      proves: "Besökstiden ändras först när planeraren har tagit ställning.",
    },
    en: {
      alt: "Requests for a new visit time from clients",
      shows: "Clients' requests for a different time for a visit.",
      proves: "The visit time changes only once the planner has decided.",
    },
  }),
  entry({
    id: "operations.published-plan-candidate-review",
    module: "Ändring av den publicerade planen",
    opens: `${INBOXES} → Granska och tillämpa`,
    setup: [
      "Förbered en justering av den publicerade planen",
      "Öppna schemat med den förberedda justeringen vald",
    ],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "En förberedd ändring av den publicerade planen som väntar på granskning",
      shows:
        "Kortet för en förberedd ändring av den publicerade planen, med vägen till granskning och tillämpning.",
      proves:
        "En ändring når den publicerade planen bara genom planerarens granskning.",
    },
    en: {
      alt: "A prepared change to the published plan waiting for review",
      shows:
        "The card for a prepared change to the published plan, with the way to review and apply it.",
      proves:
        "A change reaches the published plan only through the planner's review.",
    },
  }),
];
